import React, { Suspense, useState, useRef, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useGLTF, OrbitControls, Stage, Float, Html, ContactShadows } from '@react-three/drei';
import { QRCodeSVG } from 'qrcode.react';
import * as THREE from 'three';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@600,700&display=swap');

:root {
  --navy: #06112b; 
  --blue: #1c73ba; 
  --gold: #f5a623;
  --muted: #94a3b8; 
  --border: rgba(28, 115, 186, 0.2);
  --glass: rgba(255, 255, 255, 0.02);
}

.kr-container {
  background: var(--navy);
  background: radial-gradient(circle at 0% 0%, #112359 0%, var(--navy) 50%, #040a1f 100%);
  color: #fff; font-family: 'DM Sans', sans-serif; min-height: 100vh;
}

.kr-hero {
  padding: 120px 5% 40px; max-width: 1200px; margin: 0 auto; text-align: left;
}

.kr-hero h1 { 
  font-family: 'Clash Display', sans-serif; 
  font-size: clamp(3rem, 8vw, 5rem); 
  line-height: 0.9; margin-bottom: 20px;
  letter-spacing: -2px;
}
.kr-hero h1 span { color: var(--gold); display: block; }

.kr-grid {
  display: grid; 
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 40px; padding: 40px 5% 100px; max-width: 1400px; margin: 0 auto;
}

.kr-card {
  position: relative;
  background: var(--glass);
  border: 1px solid var(--border);
  border-radius: 32px;
  backdrop-filter: blur(20px);
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kr-card:hover {
  border-color: var(--blue);
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-12px);
  box-shadow: 0 40px 80px rgba(0,0,0,0.5);
}

.canvas-container { 
  width: 100%; height: 350px; 
  position: relative;
  background: radial-gradient(circle at center, rgba(28, 115, 186, 0.1) 0%, transparent 70%);
}

/* Flip hint badge */
.flip-hint {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(28, 115, 186, 0.85);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: 20px;
  pointer-events: none;
  backdrop-filter: blur(8px);
  z-index: 10;
  white-space: nowrap;
}

.card-inner { padding: 30px; flex-grow: 1; display: flex; flex-direction: column; }

.variant-picker {
  display: flex; gap: 10px; margin-bottom: 20px; align-items: center;
}

.variant-dot {
  width: 24px; height: 24px; border-radius: 50%; cursor: pointer;
  border: 2px solid transparent; transition: 0.2s;
}
.variant-dot.active { border-color: #fff; transform: scale(1.2); }

.feature-tag {
  font-size: 10px; font-weight: 700; color: var(--gold);
  text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;
}

.btn-whatsapp {
  margin-top: auto;
  padding: 16px; border-radius: 14px;
  background: var(--blue); color: #fff;
  font-weight: 700; text-align: center;
  text-decoration: none; transition: 0.3s;
  display: flex; align-items: center; justify-content: center; gap: 10px;
}
.btn-whatsapp:hover { background: var(--gold); color: var(--navy); }

.loader-text { color: var(--blue); font-size: 10px; text-transform: uppercase; letter-spacing: 2px; }
`;

// ─── QR Canvas Texture Generator ─────────────────────────────────────────────
// Renders a QR code into an offscreen canvas and returns a THREE.CanvasTexture.
function buildQRTexture(url, size = 512) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // White background (card back)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  // Draw QR via an offscreen SVG rendered into an Image
  // We use a tiny helper SVG → blob → Image approach
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svgEl.setAttribute('width', String(size));
  svgEl.setAttribute('height', String(size));

  // QR modules via qrcode-generator (inline fallback: use a prebuilt Data URL instead)
  // Because we can't call QRCodeSVG imperatively, we'll use the `qrcode` npm package
  // approach via canvas. We'll use the global qrcode if available, else draw a placeholder.
  try {
    // eslint-disable-next-line no-undef
    const QRCode = require('qrcode');
    QRCode.toCanvas(canvas, url, {
      width: size,
      margin: 3,
      color: { dark: '#06112b', light: '#ffffff' },
    });
  } catch {
    // Fallback: draw a simple placeholder grid pattern if qrcode pkg not available
    ctx.fillStyle = '#06112b';
    const cell = size / 25;
    for (let r = 0; r < 25; r++) {
      for (let c = 0; c < 25; c++) {
        if (Math.random() > 0.5) ctx.fillRect(c * cell, r * cell, cell - 1, cell - 1);
      }
    }
  }

  return new THREE.CanvasTexture(canvas);
}

// ─── Flap Model with QR on Back ──────────────────────────────────────────────
function FlapModel({ path, qrUrl }) {
  const { scene } = useGLTF(process.env.PUBLIC_URL + path);
  const hasApplied = useRef(false);

  useEffect(() => {
    if (hasApplied.current) return;
    hasApplied.current = true;

    const qrTexture = buildQRTexture(qrUrl);
    // Flip the texture horizontally so it reads correctly on the back face
    qrTexture.wrapS = THREE.RepeatWrapping;
    qrTexture.repeat.x = -1;
    qrTexture.offset.x = 1;

    scene.traverse((child) => {
      if (!child.isMesh) return;

      // Detect the "back" mesh by name or by its world-space Z direction.
      // Common naming conventions in card GLBs: "back", "Back", "rear", "Rear"
      const name = child.name.toLowerCase();
      const isBack =
        name.includes('back') ||
        name.includes('rear') ||
        name.includes('b_') ||
        name.includes('_b');

      if (isBack) {
        // Replace map with QR texture, keep other PBR props
        child.material = child.material.clone();
        child.material.map = qrTexture;
        child.material.roughness = 0.35;
        child.material.metalness = 0.1;
        child.material.needsUpdate = true;
      } else {
        child.material = child.material.clone();
        child.material.metalness = 0.5;
        child.material.roughness = 0.4;
      }
    });
  }, [scene, qrUrl]);

  return <primitive object={scene} />;
}

// ─── Generic Model (no QR) ───────────────────────────────────────────────────
function Model({ path }) {
  const { scene } = useGLTF(process.env.PUBLIC_URL + path);
  scene.traverse((child) => {
    if (child.isMesh) {
      child.material.metalness = 0.5;
      child.material.roughness = 0.4;
    }
  });
  return <primitive object={scene} />;
}

// ─── QR Plane — fallback plane glued to back face center ─────────────────────
// If the GLB has no named "back" mesh, we inject a flat plane with the QR texture
// positioned just behind the card (negative Z offset).
function QRBackPlane({ qrUrl }) {
  const meshRef = useRef();

  useEffect(() => {
    if (!meshRef.current) return;
    const tex = buildQRTexture(qrUrl);
    tex.wrapS = THREE.RepeatWrapping;
    tex.repeat.x = -1;
    tex.offset.x = 1;
    meshRef.current.material.map = tex;
    meshRef.current.material.needsUpdate = true;
  }, [qrUrl]);

  return (
    <mesh ref={meshRef} position={[0, 0, -0.021]} rotation={[0, Math.PI, 0]}>
      {/* Plane sized to standard CR80 card ratio ~1.586:1, scaled to match GLB units */}
      <planeGeometry args={[1.35, 0.85]} />
      <meshStandardMaterial
        color="#ffffff"
        roughness={0.3}
        metalness={0.0}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Keyrings() {
  const [variants, setVariants] = useState({
    uno: { file: '/models/unored.glb', name: 'Red' },
    flap: { file: '/models/flap.glb', name: 'Classic' },
  });

  const products = [
    {
      id: 'flap',
      name: 'Flap Original',
      desc: 'Our flagship design. Features a precision CNC-relief on front and a custom-engraved QR on the back.',
      tags: ['Signature', 'NFC + QR Hybrid'],
      qrUrl: 'https://www.flaap.me/link-flap',
      hasQR: true,
      variants: [
        { name: 'Classic', color: '#1c73ba', file: '/models/flap.glb' },
        { name: 'Walnut Wood', color: '#63412b', file: '/models/flapwood.glb' },
      ],
    },
    {
      id: 'uno',
      name: 'Uno Collection',
      desc: 'The nostalgic card game brought to 3D life. Pick your lucky color and tap to share.',
      tags: ['Playful', 'Gaming'],
      variants: [
        { name: 'Red', color: '#dc3545', file: '/models/unored.glb' },
        { name: 'Blue', color: '#007bff', file: '/models/unoblue.glb' },
        { name: 'Yellow', color: '#ffc107', file: '/models/unoyellow.glb' },
        { name: 'Green', color: '#28a745', file: '/models/unogreen.glb' },
      ],
    },
    {
      id: 'insta',
      name: 'Social Tag',
      file: '/models/instagram.glb',
      desc: 'Instant Instagram networking. A sleek, curved design that fits every aesthetic.',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Tiktok',
      name: 'Social Tag',
      file: '/models/tiktok.glb',
      desc: '',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Whatsapp',
      name: 'Social Tag',
      file: '/models/whatsapp.glb',
      desc: '',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Youtube',
      name: 'Social Tag',
      file: '/models/youtube.glb',
      desc: '',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Github',
      name: 'Social Tag',
      file: '/models/github.glb',
      desc: '',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Bike Numberplate',
      name: 'Bike Numberplatebik',
      file: '/models/numberplate.glb',
      desc: 'Instant Instagram networking. A sleek, curved design that fits every aesthetic.',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'Nepal Map',
      name: 'Social Tag',
      file: '/models/nepalmap.glb',
      desc: 'Instant Instagram networking. A sleek, curved design that fits every aesthetic.',
      tags: ['Social', 'NFC Only'],
    },
    {
      id: 'nepal',
      name: 'Heritage Flag',
      file: '/models/nepalflag.glb',
      desc: 'Pride of Nepal. Intricate double-triangle relief for a tactile, powerful feel.',
      tags: ['Limited Edition', 'Heritage'],
    },
    {
      id: 'naruto',
      name: 'Shadow Clone',
      file: '/models/naruto.glb',
      desc: 'For the Shinobi. A bold anime-inspired silhouette with deep 3D engraved lines.',
      tags: ['Anime', 'Collectable'],
    },
    {
      id: 'cat',
      name: 'Cat Lover',
      file: '/models/cat.glb',
      desc: 'Instant pet profile sharing. Smooth 3D curves designed for animal enthusiasts.',
      tags: ['NFC', 'Pet Tech'],
    },
    {
      id: 'Wolf',
      name: 'Lone Wolf',
      file: '/models/wolf.glb',
      desc: 'Embodying the spirit of independence. Perfect for the solo adventurer.',
      tags: ['NFC', 'Adventure'],
    },
    {
      id: 'Panda',
      name: 'Panda Pal',
      file: '/models/panda.glb',
      desc: 'Your adorable companion for sharing contact info. A cute, rounded design with a friendly vibe.',
      tags: ['NFC', 'Adventure'],
    },
    {
      id: 'Skull',
      name: 'Rebel Skull',
      file: '/models/skull.glb',
      desc: 'A bold statement piece with a rebellious edge for those who dare to be different.',
      tags: ['NFC', 'Bold Style'],
    },
  ];

  const handleVariantChange = (prodId, file, name) => {
    setVariants({ ...variants, [prodId]: { file, name } });
  };

  const getWhatsAppLink = (pName, vName) => {
    const text = encodeURIComponent(
      `Hi Flaap! I want to order the ${pName} Keyring ${vName ? `(${vName} Variant)` : ''}.`
    );
    return `https://wa.me/9779802365432?text=${text}`;
  };

  return (
    <div className="kr-container">
      <style>{STYLES}</style>

      <section className="kr-hero">
        <h1>
          Smart <span>Keyrings.</span>
        </h1>
        <p style={{ color: 'var(--muted)', maxWidth: '550px', lineHeight: '1.7' }}>
          Interactive 3D showcases of our NFC collection. Each piece is precision crafted using
          IGES data for flawless CNC carving.
        </p>
      </section>

      <div className="kr-grid">
        {products.map((p) => {
          const currentFile = p.variants ? variants[p.id]?.file : p.file;
          const currentVariantName = p.variants ? variants[p.id]?.name : '';

          return (
            <div className="kr-card" key={p.id}>
              <div className="canvas-container">
                {/* Flip hint only for Flap Original */}
                {p.hasQR && (
                  <div className="flip-hint">↻ NFC</div>
                )}

                <Canvas camera={{ position: [0, 0, 5], fov: 40 }}>
                  <ambientLight intensity={0.8} />
                  <pointLight position={[5, 5, 5]} />
                  <Suspense
                    fallback={
                      <Html center>
                        <div className="loader-text">Loading...</div>
                      </Html>
                    }
                  >
                    <Stage environment="city" intensity={0.4} contactShadow={false}>
                      <Float speed={4} rotationIntensity={1} floatIntensity={1.5}>
                        {p.hasQR ? (
                          // Flap Original: inject QR plane on back + apply texture to back mesh
                          <group>
                            <FlapModel path={currentFile} qrUrl={p.qrUrl} />
                            {/* QRBackPlane acts as a guaranteed fallback if no "back" mesh found */}
                            <QRBackPlane qrUrl={p.qrUrl} />
                          </group>
                        ) : (
                          <Model key={currentFile} path={currentFile} />
                        )}
                      </Float>
                    </Stage>
                    <ContactShadows opacity={0.4} scale={10} blur={2} far={1} />
                    <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={2} />
                  </Suspense>
                </Canvas>
              </div>

              <div className="card-inner">
                <span className="feature-tag">{p.tags.join(' • ')}</span>
                <h3 style={{ fontFamily: 'Clash Display', margin: '0 0 10px 0' }}>{p.name}</h3>
                <p
                  style={{
                    color: 'var(--muted)',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    marginBottom: '20px',
                  }}
                >
                  {p.desc}
                </p>

                {p.variants && (
                  <div className="variant-picker">
                    <span style={{ fontSize: '12px', color: 'var(--muted)', marginRight: '10px' }}>
                      Select:
                    </span>
                    {p.variants.map((v) => (
                      <div
                        key={v.name}
                        className={`variant-dot ${currentVariantName === v.name ? 'active' : ''}`}
                        style={{ backgroundColor: v.color }}
                        onClick={() => handleVariantChange(p.id, v.file, v.name)}
                        title={v.name}
                      />
                    ))}
                  </div>
                )}

                <a
                  href={getWhatsAppLink(p.name, currentVariantName)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-whatsapp"
                >
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.588-5.946 0-6.556 5.332-11.888 11.888-11.888 3.176 0 6.164 1.236 8.41 3.483 2.247 2.247 3.483 5.234 3.483 8.41 0 6.556-5.332 11.89-11.888 11.89-1.999 0-3.959-.502-5.713-1.453l-6.279 1.667zm6.002-3.666l.43.255c1.479.878 3.187 1.341 4.954 1.341 5.418 0 9.825-4.407 9.825-9.825s-4.407-9.825-9.825-9.825c-2.627 0-5.1.1.022-6.963 1.921-1.862 1.862-5.042 3.483-8.41-3.177 0-5.418 4.407-9.825 9.825 0 1.735.452 3.43 1.309 4.93l.28.487-1.107 4.04 4.143-1.102z" />
                  </svg>
                  Order via WhatsApp
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}